import { useEffect, useState } from 'react';
import { History, RotateCcw, X } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { cn } from '@/lib/utils';

export interface EditorVersionSummary {
  id: string;
  /** When the checkpoint was taken (ISO). */
  savedAt: string;
  label: string;
  detail?: string;
  /** False for read-only entries (e.g. published git commits) — the Restore
   *  button disables while one is selected. Defaults to restorable. */
  restorable?: boolean;
}

export interface EditorVersionDetail {
  id: string;
  /** Composed markdown for the preview pane. */
  content: string;
}

export interface EditorHistorySource {
  list: () => Promise<EditorVersionSummary[]>;
  get: (id: string) => Promise<EditorVersionDetail>;
  /** Restoring re-saves the checkpointed content; omit for view-only history. */
  restore?: (id: string) => Promise<void>;
}

interface VersionHistoryModalProps {
  title: string;
  source: EditorHistorySource;
  onClose: () => void;
}

/**
 * View History for a full editor (specs/changes/consolidated-full-editor R4):
 * checkpoints newest-first on the left, rendered preview on the right,
 * restore re-saves the selected checkpoint (which itself checkpoints, so a
 * restore never destroys history).
 */
export function VersionHistoryModal({ title, source, onClose }: VersionHistoryModalProps) {
  const [versions, setVersions] = useState<EditorVersionSummary[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EditorVersionDetail | null>(null);
  const [restoring, setRestoring] = useState(false);
  const selectedRestorable =
    versions?.find((v) => v.id === selectedId)?.restorable !== false;

  useEffect(() => {
    let cancelled = false;
    source
      .list()
      .then((list) => {
        if (cancelled) return;
        setVersions(list);
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [source]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setDetail(null);
    source
      .get(selectedId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { /* the list stays usable; the pane shows the spinner state out */ });
    return () => { cancelled = true; };
  }, [selectedId, source]);

  async function handleRestore() {
    if (!selectedId || !source.restore) return;
    if (!window.confirm('Restore this checkpoint? The current content is checkpointed first, so nothing is lost.')) return;
    setRestoring(true);
    try {
      await source.restore(selectedId);
      onClose();
    } finally {
      setRestoring(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-3xl h-[80vh] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <History className="w-4 h-4 text-slate-400 shrink-0" />
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
              History — {title}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Checkpoint list */}
          <div className="w-52 sm:w-60 shrink-0 border-r border-slate-200 dark:border-slate-700 overflow-y-auto">
            {versions === null && !loadError && <LoadingState />}
            {loadError && (
              <p className="p-4 text-xs text-red-600 dark:text-red-400">Could not load the history. Close and try again.</p>
            )}
            {versions?.length === 0 && (
              <p className="p-4 text-xs text-slate-500 dark:text-slate-400">
                No checkpoints yet. This document is checkpointed as you edit and on every publish — the latest 30 stay here.
              </p>
            )}
            {versions?.map((v) => (
              <button
                key={v.id}
                onClick={() => setSelectedId(v.id)}
                className={cn(
                  'w-full text-left px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 transition-colors',
                  selectedId === v.id
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                )}
              >
                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{v.label}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {new Date(v.savedAt).toLocaleString()}
                </p>
                {v.detail && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{v.detail}</p>}
              </button>
            ))}
          </div>

          {/* Preview pane — docs-page/article pick up the shared doc typography */}
          <div className="docs-page flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
            {selectedId && !detail ? (
              <LoadingState />
            ) : detail ? (
              <article>
                <MarkdownRenderer content={detail.content} />
              </article>
            ) : (
              <p className="text-sm text-slate-400 dark:text-slate-500 italic">Select a checkpoint to preview it.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        {source.restore && (
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {selectedRestorable
                ? 'Restoring checkpoints the current content first — nothing is lost.'
                : 'Published states live in git — open the commit to see the exact files.'}
            </span>
            <button
              onClick={handleRestore}
              disabled={!selectedId || !detail || restoring || !selectedRestorable}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {restoring ? <Spinner size="sm" tone="current" /> : <RotateCcw className="w-3.5 h-3.5" />}
              {restoring ? 'Restoring…' : 'Restore this version'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
